import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, ApiError, setCsrfToken } from "../lib/api.js";
import type { LoginChallenge, StaffUser, TotpSetupResult } from "./types.js";

const ME_QUERY_KEY = ["auth", "me"] as const;

interface AuthContextValue {
  user: StaffUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginChallenge>;
  verifyTotp: (challengeId: string, code: string) => Promise<StaffUser>;
  setupTotp: (challengeId: string) => Promise<TotpSetupResult>;
  confirmTotp: (challengeId: string, code: string) => Promise<StaffUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Server state through TanStack Query, per CLAUDE.md — including "am I logged in", which is just
 * whether `GET /auth/me` succeeds. A 401 is a normal, expected outcome here (not a fetch failure),
 * so it is not retried and does not throw upward.
 */
export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const queryClient = useQueryClient();

  const meQuery = useQuery<StaffUser | null>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const user = await apiGet<StaffUser>("/auth/me");
        setCsrfToken(user.csrfToken);
        return user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
  });

  const setUser = useCallback(
    (user: StaffUser) => {
      setCsrfToken(user.csrfToken);
      queryClient.setQueryData(ME_QUERY_KEY, user);
    },
    [queryClient],
  );

  const login = useCallback(
    (email: string, password: string) =>
      apiPost<LoginChallenge>("/auth/login", { email, password }),
    [],
  );

  const verifyTotp = useCallback(
    async (challengeId: string, code: string) => {
      const user = await apiPost<StaffUser>("/auth/totp", { challengeId, code });
      setUser(user);
      return user;
    },
    [setUser],
  );

  const setupTotp = useCallback(
    (challengeId: string) => apiPost<TotpSetupResult>("/auth/totp/setup", { challengeId }),
    [],
  );

  const confirmTotp = useCallback(
    async (challengeId: string, code: string) => {
      const user = await apiPost<StaffUser>("/auth/totp/confirm", { challengeId, code });
      setUser(user);
      return user;
    },
    [setUser],
  );

  const logout = useCallback(async () => {
    await apiPost("/auth/logout");
    setCsrfToken(undefined);
    queryClient.setQueryData(ME_QUERY_KEY, null);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      login,
      verifyTotp,
      setupTotp,
      confirmTotp,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, verifyTotp, setupTotp, confirmTotp, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
